// Texto legível das razões de falha, fora do núcleo de avaliação.
//
// O núcleo decide se um elemento cumpre um facet; não decide em que
// língua se explica a falha. Separá-los permite que a mesma lógica sirva
// a plataforma (português) e o servidor MCP (inglês) sem duplicar código
// e, sobretudo, sem que os dois possam chegar a vereditos diferentes.
//
// Convenção: cada entrada devolve texto já formatado. As partes
// puramente simbólicas (>=, <=, >, <) não passam por aqui porque são
// iguais em qualquer língua.

export interface IdsMessages {
  // --- Descrição de uma restrição de valor ---------------------------
  /** Enumeração: recebe os itens já unidos por vírgula. */
  oneOf(items: string): string
  pattern(regex: string): string
  length(n: number): string
  minLength(n: number): string
  maxLength(n: number): string
  /** Ligação entre partes de uma restrição composta (com os espaços). */
  and: string
  /** Restrição vazia: qualquer valor serve. */
  anyValue: string

  // --- Vereditos de cardinalidade ------------------------------------
  mustNotBePresent(label: string): string
  isMissing(label: string, expected: string): string
  doesNotMatch(label: string, expected: string): string

  // --- Rótulos e expectativas por facet ------------------------------
  entityMismatch(expected: string): string
  /** Expectativa quando o facet só exige existência. */
  toBePresent: string
  attribute(name: string): string
  property(propertySet: string, baseName: string): string
  dataType(name: string): string
  system(value: string): string
  code(value: string): string
  classification: string
  material: string
  partOf: string
  partOfEntity(entity: string): string
  partOfAnything: string

  // --- Erros de leitura de um ficheiro .ids --------------------------
  /** Valor com casas decimais onde o dataType declarado é inteiro. */
  integerValue(value: string, dataType: string): string
  /** O mesmo, mas o inteiro vem do esquema IFC e não do IDS. */
  integerAttribute(
    value: string,
    entity: string,
    attribute: string,
    schema: string,
  ): string
  /** xs:pattern sobre um atributo que não é texto. */
  patternNotText(
    entity: string,
    attribute: string,
    schema: string,
    category: string,
  ): string
  xmlUnreadable(detail: string): string
  notAnIds: string

  // --- Saídas do relatório: BCF e CSV --------------------------------
  // Estas atravessam a fronteira para outras ferramentas, por isso o
  // texto português vai deliberadamente sem acentos.
  bcfTitle(specName: string, failures: number): string
  bcfNotMet(failures: number, applicable: number): string
  bcfMore(hidden: number): string
  csvHeader: readonly string[]
  csvFail: string
  csvNotEvaluated: string

  // --- Progresso e notas do adaptador --------------------------------
  progressIndexing: string
  progressValidating(index: number, total: number, specName: string): string
  /** Spec cuja applicability não parte de uma classe IFC concreta: não há
   *  por onde começar a procurar elementos, logo não se avalia. */
  noteApplicabilityNotConcrete: string
}

/** Inglês. Omissão do pacote: é a língua da especificação IDS e a que o
 *  servidor MCP usa nos relatórios. */
export const MESSAGES_EN: IdsMessages = {
  oneOf: (items) => `one of [${items}]`,
  pattern: (regex) => `pattern /${regex}/`,
  length: (n) => `length ${n}`,
  minLength: (n) => `length >= ${n}`,
  maxLength: (n) => `length <= ${n}`,
  and: ' and ',
  anyValue: 'any value',

  mustNotBePresent: (label) => `${label} must not be present`,
  isMissing: (label, expected) => `${label} is missing (expected ${expected})`,
  doesNotMatch: (label, expected) =>
    `${label} does not match (expected ${expected})`,

  entityMismatch: (expected) =>
    `entity/predefinedType does not match (expected ${expected})`,
  toBePresent: 'to be present',
  attribute: (name) => `attribute ${name}`,
  property: (propertySet, baseName) => `property ${propertySet}.${baseName}`,
  dataType: (name) => `data type ${name}`,
  system: (value) => `system ${value}`,
  code: (value) => `code ${value}`,
  classification: 'classification',
  material: 'material',
  partOf: 'partOf',
  partOfEntity: (entity) => `part of ${entity}`,
  partOfAnything: 'part of something',

  integerValue: (value, dataType) =>
    `Invalid IDS: the value "${value}" has a decimal part but the declared ` +
    `dataType ${dataType} is an integer.`,
  integerAttribute: (value, entity, attribute, schema) =>
    `Invalid IDS: the value "${value}" has a decimal part but the attribute ` +
    `${entity}.${attribute} (${schema}) is an integer.`,
  patternNotText: (entity, attribute, schema, category) =>
    `Invalid IDS: xs:pattern only applies to text, but the attribute ` +
    `${entity}.${attribute} (${schema}) is ${category}.`,
  xmlUnreadable: (detail) =>
    `Invalid IDS file: the XML could not be read (${detail}).`,
  notAnIds: 'This file is not an IDS (the root <ids> element is missing).',

  bcfTitle: (specName, failures) =>
    `IDS: ${specName} (${failures} failure${failures === 1 ? '' : 's'})`,
  bcfNotMet: (failures, applicable) =>
    `IDS specification not met by ${failures} of ${applicable} applicable elements.`,
  bcfMore: (hidden) => `\n(+${hidden} more; see the CSV)`,
  csvHeader: ['Specification', 'Status', 'GlobalId', 'Class', 'Name', 'Reasons'],
  csvFail: 'FAIL',
  csvNotEvaluated: 'NOT_EVALUATED',

  progressIndexing: 'Indexing properties...',
  progressValidating: (index, total, specName) =>
    `Validating ${index}/${total}: ${specName}`,
  noteApplicabilityNotConcrete:
    'The applicability of this specification does not start from a ' +
    'concrete IFC class (an entity facet with an exact name or an ' +
    'enumeration); not evaluated in this version.',
}

/** Português de Portugal. É o que a plataforma mostra ao utilizador, e o
 *  texto é o que já estava em produção antes de o núcleo ser partilhado:
 *  unificar o motor não devia mudar uma única palavra na interface. */
export const MESSAGES_PT: IdsMessages = {
  oneOf: (items) => `um de [${items}]`,
  pattern: (regex) => `padrão /${regex}/`,
  length: (n) => `comprimento ${n}`,
  minLength: (n) => `comprimento >= ${n}`,
  maxLength: (n) => `comprimento <= ${n}`,
  and: ' e ',
  anyValue: 'qualquer valor',

  mustNotBePresent: (label) => `${label} não devia existir`,
  isMissing: (label, expected) => `${label} em falta (esperado ${expected})`,
  doesNotMatch: (label, expected) =>
    `${label} não corresponde (esperado ${expected})`,

  entityMismatch: (expected) =>
    `classe/predefinedType não corresponde (esperado ${expected})`,
  toBePresent: 'existir',
  attribute: (name) => `atributo ${name}`,
  property: (propertySet, baseName) => `propriedade ${propertySet}.${baseName}`,
  dataType: (name) => `tipo ${name}`,
  system: (value) => `sistema ${value}`,
  code: (value) => `código ${value}`,
  classification: 'classificação',
  material: 'material',
  partOf: 'partOf',
  partOfEntity: (entity) => `parte de ${entity}`,
  partOfAnything: 'parte de algo',

  integerValue: (value, dataType) =>
    `IDS inválido: o valor "${value}" tem parte decimal mas o dataType ` +
    `${dataType} é inteiro.`,
  integerAttribute: (value, entity, attribute, schema) =>
    `IDS inválido: o valor "${value}" tem parte decimal mas o atributo ` +
    `${entity}.${attribute} (${schema}) é inteiro.`,
  patternNotText: (entity, attribute, schema, category) =>
    `IDS inválido: xs:pattern só se aplica a texto, mas o atributo ` +
    `${entity}.${attribute} (${schema}) é ${category}.`,
  // A versão anterior dizia só "(malformado)"; passar o detalhe do leitor
  // XML diz onde falhou, que é o que serve para corrigir o ficheiro.
  xmlUnreadable: (detail) =>
    `Ficheiro IDS inválido: o XML não pôde ser lido (${detail}).`,
  notAnIds: 'Este ficheiro não é um IDS (falta o elemento raiz <ids>).',

  bcfTitle: (specName, failures) =>
    `IDS: ${specName} (${failures} falha${failures === 1 ? '' : 's'})`,
  bcfNotMet: (failures, applicable) =>
    `Especificacao IDS nao cumprida por ${failures} de ${applicable} ` +
    `elementos aplicaveis.`,
  bcfMore: (hidden) => `\n(+${hidden} mais; ver CSV)`,
  csvHeader: ['Especificacao', 'Estado', 'GlobalId', 'Classe', 'Nome', 'Razoes'],
  csvFail: 'FALHA',
  csvNotEvaluated: 'NAO_AVALIADA',

  progressIndexing: 'A indexar propriedades...',
  progressValidating: (index, total, specName) =>
    `A validar ${index}/${total}: ${specName}`,
  noteApplicabilityNotConcrete:
    'A applicability desta spec não começa por uma classe IFC ' +
    'concreta (entity com nome exato ou lista); não avaliada nesta ' +
    'versão.',
}
